/* Environmental (Tab Mods) */

import { css } from 'styled-components';
import theme from 'theme/theme';
import { media } from 'theme/utils';
import { styleBase, styleFeatures, styleHero, styleNoImages } from '../styles';

const { colors } = theme;

const Hero = {
  key: 'Hero',
  styledComponent: css`
    ${styleHero}

    &&& {
      padding-bottom: 11.8rem;
      background-size: contain;
      background-position: bottom 0 center;

      ${media.md`
        padding-bottom: 2.6rem;
        background-position: bottom 0 right -11.5rem;

        div[class*='descriptionWrapper'] {
          width: 23em;
          padding-right: 2.8em;
        }
      `}

      ${media.lg`
        padding: 0;
        background-position: bottom 0 right -20rem;

        div[class*='descriptionWrapper'] {
          width: unset;
          max-width: 29em;
          padding-right: 0;
        }
      `}
    }
  `,
};

const Nav = {
  key: 'Nav',
  styledComponent: css`
    ${styleFeatures}

    &&& {
      background-color: ${colors.lime};
    }
  `,
};

const Stewardship = {
  key: 'Stewardship',
  styledComponent: css`
    ${styleBase}
    ${styleNoImages}

    &&& {
      h1 {
        margin-top: 0;
        text-align: center;
      }

      div[class*='featuresList'] {
        margin: 2.6em 0 3.5em;
        padding: 0 !important;

        div[class*='feature'] {
          margin-bottom: 2.8em;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      ${media.md`
        div[class*='descriptionWrapper'] {
          max-width: 32em;
          text-align: justify;
        }

        div[class*='featuresList'] {
          margin-top: 1.7em;
          justify-content: space-around;

          div[class*='feature'] {
            align-items: center;
            margin: 0;
          }
        }
      `}

      ${media.lg`
        div[class*='descriptionWrapper'] {
          max-width: 36em;
        }
      `}
    }
  `,
};

const Neutrality = {
  key: 'Neutrality',
  styledComponent: css`
    ${Stewardship.styledComponent}

    &&& {
      padding-top: 0em;

      ${media.md`
        padding-top: 2em;
      `}
    }
  `,
};

const Emissions = {
  key: 'Emissions',
  styledComponent: css`
    ${styleBase}
    ${styleNoImages}

    &&& {
      padding: 0 2em;

      div[class*='content'] {
        padding-top: 2em;
        border-top: 1px solid lightgray;
      }

      div[class*='descriptionWrapper'] {
        ul {
          display: flex;
          flex-flow: row wrap;
          justify-content: space-around;
          margin: 0;
          padding: 0;
          list-style: none;

          li {
            margin: 0.8em 0;

            > div div:first-child {
              flex-flow: column-reverse;
              align-items: center;
              font-size: 1.125em;
              font-weight: 700;
              line-height: 1.2;
              text-align: center;

              img {
                margin: 1em 0;
              }

              span {
                font-weight: 800;
              }
            }
          }
        }
      }

      ${media.md`
        padding: 0 4em;

        div[class*='descriptionWrapper'] {
          width: 100%;
          max-width: unset;

          ul {
            li {
              flex: 0 1 50%;
              margin: 3.5em 0;

              > div div:first-child {
                align-items: flex-start;
                max-width: 13em;
                margin: 0 auto;
                text-align: left;

                img {
                  margin: 1.2em 0;
                }

                span {
                  height: 2.4em;
                }
              }
            }
          }
        }
      `}

      ${media.lg`
        div[class*='descriptionWrapper'] ul {
          flex-wrap: nowrap;

          li {
            flex: unset;
          }
        }
      `}
    }
  `,
};

const Efficiencies = {
  key: 'Efficiencies',
  styledComponent: css`
    ${styleFeatures}

    &&& {
      padding-bottom: 1em;

      // footnote
      i > u {
        text-decoration: none;
        font-variant: super;
      }

      div[class*='featuresList'] {
        div[class*='feature'] {
          padding-top: 3em;
          border-top: 1px solid lightgray;

          img {
            max-width: 9.4em;
          }
        }
      }

      ${media.md`
        div[class*='featuresList'] {

          div[class*='feature'] {
            align-items: flex-start;

            &:nth-child(2n-1) {
              flex-direction: row-reverse;

              div[class*='description'] {
                margin: 0 3em 0 0;
              }
            }
          }
        }
      `}

      ${media.lg`
        padding-top: 0;

        div[class*='featuresList'] {
          div[class*='feature'] {
          }
        }
      `}
    }
  `,
};

const Recycling = {
  key: 'Recycling',
  styledComponent: css`
    ${Stewardship.styledComponent}

    &&& {
    }
  `,
};

const Packaging = {
  key: 'Packaging',
  styledComponent: css`
    ${Efficiencies.styledComponent}

    &&& {
      padding-top: 0;
      padding-bottom: 5em;
    }
  `,
};

const Footnotes = {
  key: 'Footnotes',
  styledComponent: css`
    &&& {
      background-color: ${colors.white};

      div[class*='descriptionWrapper'] {
        align-items: flex-start;
        width: 100%;
        max-width: 49em;

        ol {
          list-style: decimal;

          li::marker {
            font-style: italic;
            font-variant: super;
          }
        }
      }
    }
  `,
};

export const Environmental = {
  key: '/responsibility-environmental',
  content: [
    Hero,
    Nav,
    Stewardship,
    Neutrality,
    Emissions,
    Efficiencies,
    Recycling,
    Packaging,
    Footnotes,
  ],
};

export default Environmental;
